<template>
  <div class="news-app">
    <Preloader :backgroundColor="preloaderColor"></Preloader>

    <!-- Header -->
    <HeaderNews
      subtitle="Noticias y contenido sobre Inteligencia Artificial"
      :showSearch="true"
      :searchQuery.sync="query"
      :showMobileMenu="true"
      @toggle-mobile-menu="toggleMobileMenu"
    />

    <!-- Main Layout -->
    <div class="news-layout">
      <!-- Sidebar -->
      <Sidebar :class="{'sidebar--mobile-visible': showMobileSidebar}" @navigate="showMobileSidebar = false" @toggle-collapse="handleSidebarCollapse" />

      <!-- Mobile Overlay -->
      <div
        v-if="showMobileSidebar"
        class="mobile-overlay"
        @click="toggleMobileMenu"
      ></div>

      <!-- Main Content -->
      <div class="news-body">
      <main class="news-main" ref="newsMain">
        <div class="news-main__container">
        <!-- Header de HuggingFace -->
        <div class="huggingface-header">
          <div class="huggingface-logo">
            <img src="/assets/img/companies/huggingface_logo.svg" alt="HuggingFace" />
          </div>
          <div class="huggingface-content">
            <div class="huggingface-title-row">
              <h1 class="huggingface-title">HuggingFace</h1>
              <a
                href="https://huggingface.co/"
                target="_blank"
                rel="noopener noreferrer"
                class="huggingface-link-btn"
                title="Visitar HuggingFace"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
            <p class="huggingface-description">
              HuggingFace es la plataforma líder en código abierto para Machine Learning.
              Ofrece miles de modelos preentrenados, datasets y herramientas para procesamiento
              de lenguaje natural, visión por computadora y más. Una comunidad global colabora
              para democratizar la inteligencia artificial.
            </p>
          </div>
        </div>

        <!-- Content scrollable -->
        <section class="news-content" ref="scrollContainer">
          <!-- Error Message -->
          <div v-if="error" class="error-message">
            <div class="error-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <div class="error-content">
              <h4>Error al cargar noticias</h4>
              <p>{{ error }}</p>
            </div>
          </div>

          <div class="section-header">
            <div class="section-header__icon">
              <img src="/assets/icons/news.png" alt="Artículos" width="24" height="24">
            </div>
            <div class="section-header__main">
              <div class="section-header__top">
                <h2 class="section-title">Artículos</h2>
                <span class="section-count">{{ totalCount }} items</span>
              </div>
              <p class="section-description">Publicaciones del blog de HuggingFace y su comunidad</p>
            </div>
          </div>

          <!-- Feed List -->
          <FeedList :items="filteredItems" :loading="initialLoading" layout="blog" />

          <!-- Loading más items -->
          <div v-if="loadingMore && !initialLoading" class="loading-more">
            <div class="loading-spinner"></div>
            <span>Cargando más contenido...</span>
          </div>

          <!-- Botón Cargar Más -->
          <div v-if="!loadingMore && !initialLoading && currentChunk < totalChunks && visible.length > 0" class="load-more-container">
            <button @click="loadChunk" class="load-more-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              <span>Cargar más contenido ({{ currentChunk }}/{{ totalChunks }})</span>
            </button>
          </div>

          <!-- Fin de resultados -->
          <div v-if="!loadingMore && currentChunk >= totalChunks && visible.length > 0" class="end-message">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Has visto todo el contenido disponible</span>
          </div>
        </section>
        </div>
      </main>

      <!-- Footer -->
      <Footer />
      </div>
    </div>

    <!-- Botón scroll to top -->
    <button
      @click="scrollToTop"
      class="scroll-to-top"
      :class="{'scroll-to-top--visible': showScrollTop}"
      aria-label="Volver al inicio"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    </button>
  </div>
</template>

<script>
import HeaderNews from '../components/HeaderNews';
import Sidebar from '../components/Sidebar';
import FeedList from '../components/FeedList';
import Footer from '../components/Footer';
import Preloader from '../../../components/Preloader';
import feedChunksConfig from '../../../config/feedChunks.config';

export default {
  name: 'HuggingFace',
  components: {
    HeaderNews,
    Sidebar,
    FeedList,
    Footer,
    Preloader
  },
  data() {
    return {
      // Datos de chunks
      loadedItems: [],           // Todos los items cargados
      currentChunk: 0,            // Chunk actual
      totalChunks: 0,             // Total de chunks disponibles
      totalCount: 0,              // Total de noticias

      // Estados de carga
      initialLoading: false,      // Primera carga
      loadingMore: false,         // Cargando más chunks
      error: '',

      // Filtros
      query: '',
      metadata: null,             // Metadata completa del feed

      // Scroll to top
      showScrollTop: false,

      // Responsive
      windowWidth: window.innerWidth,

      // Mobile sidebar
      showMobileSidebar: false,

      preloaderColor: 'rgba(14, 18, 23, 0.8)',
    };
  },
  computed: {
    // Items filtrados por búsqueda
    filteredItems() {
      let filtered = this.loadedItems;

      // Filtro por texto
      const q = this.query.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter(it =>
          (it.title && it.title.toLowerCase().includes(q)) ||
          (it.description && it.description.toLowerCase().includes(q))
        );
      }

      return filtered;
    },

    // Backward compatibility
    visible() {
      return this.filteredItems;
    }
  },
  methods: {
    async loadMetadata() {
      try {
        const metadataUrl = `${feedChunksConfig.baseUrl}metadata.json`;
        const response = await fetch(metadataUrl);
        if (!response.ok) throw new Error('No se pudo cargar metadata');
        this.metadata = await response.json();

        // Obtener información de HuggingFace
        const categoriesMetadata = this.metadata.categories.notice || [];
        const huggingFaceMeta = categoriesMetadata.find(c => c.category === 'HuggingFace');

        if (huggingFaceMeta) {
          this.totalChunks = huggingFaceMeta.totalChunks || 0;
          this.totalCount = huggingFaceMeta.count || 0;
        }

        console.log('📊 Metadata cargada para HuggingFace:', { totalChunks: this.totalChunks, totalCount: this.totalCount });
      } catch (e) {
        console.error('Error cargando metadata:', e);
        throw e;
      }
    },

    async loadChunk() {
      if (this.loadingMore || this.currentChunk >= this.totalChunks) {
        console.log(`⏸️ No se carga chunk: loadingMore=${this.loadingMore}, currentChunk=${this.currentChunk}, totalChunks=${this.totalChunks}`);
        return;
      }

      const isFirstLoad = this.currentChunk === 0;
      if (isFirstLoad) {
        this.initialLoading = true;
      } else {
        this.loadingMore = true;
      }

      this.error = '';

      try {
        let items = [];

        // Cargar chunk de HuggingFace
        const chunkFile = `notice-HuggingFace-chunk-${this.currentChunk}.json`;
        const chunkUrl = `${feedChunksConfig.baseUrl}${chunkFile}`;
        const response = await fetch(chunkUrl);

        if (response.ok) {
          items = await response.json();
          console.log(`✅ Cargado: ${chunkFile} (${items.length} items)`);
        } else {
          console.warn(`⚠️ No se pudo cargar ${chunkFile}`);
          this.error = 'No se pudo cargar el contenido';
          return;
        }

        // Transformar items al formato esperado
        const transformedItems = items.map(item => {
          const uniqueId = item.id || item.link || `fallback-${item.title}-${item.pubDate}`;

          return {
            id: uniqueId,
            title: item.title || '',
            link: item.link || '',
            pubDate: item.pubDate ? new Date(item.pubDate) : null,
            description: item.description || '',
            category: item.sourceCategory || 'HuggingFace',
            sourceCategory: item.sourceCategory || 'HuggingFace',
            sourceType: item.sourceType || 'notice',
            sourceTitle: item.sourceTitle || 'HuggingFace',
            sourceCountry: item.sourceCountry || '',
            author: item.author || 'Desconocido',
            content: item.content || '',
            image: item.image || null,
            isShortVideo: item.isShortVideo || 0
          };
        });

        // Agregar nuevos items
        this.loadedItems.push(...transformedItems);

        // Remover duplicados
        const uniqueItemsMap = new Map();
        this.loadedItems.forEach(item => {
          if (!uniqueItemsMap.has(item.link)) {
            uniqueItemsMap.set(item.link, item);
          }
        });

        const beforeCount = this.loadedItems.length;
        this.loadedItems = Array.from(uniqueItemsMap.values());
        const afterCount = this.loadedItems.length;
        const duplicatesRemoved = beforeCount - afterCount;

        this.currentChunk++;

        console.log(`✅ Chunk ${this.currentChunk - 1} cargado (${items.length} items, ${duplicatesRemoved} duplicados removidos). Total único: ${this.loadedItems.length}`);

      } catch (e) {
        this.error = e.message || 'Error desconocido al cargar noticias';
        console.error('Error en loadChunk:', e);
      } finally {
        this.initialLoading = false;
        this.loadingMore = false;
      }
    },

    handleScroll(event) {
      const element = event.target;
      const scrollTop = element.scrollTop;
      this.showScrollTop = scrollTop > 50;
    },

    scrollToTop() {
      if (this.$refs.newsMain) {
        this.$refs.newsMain.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      if (this.windowWidth >= 768) {
        this.showMobileSidebar = false;
      }
    },

    toggleMobileMenu() {
      this.showMobileSidebar = !this.showMobileSidebar;
    },

    handleSidebarCollapse(isCollapsed) {
      console.log('Sidebar collapsed:', isCollapsed);
    },

    async refresh() {
      console.log('🔄 Cargando datos de HuggingFace...');

      // Reset estado
      this.loadedItems = [];
      this.currentChunk = 0;
      this.totalChunks = 0;
      this.totalCount = 0;
      this.error = '';
      this.initialLoading = true;

      try {
        // Cargar metadata
        await this.loadMetadata();

        // Cargar primer chunk
        await this.loadChunk();

      } catch (e) {
        this.error = e.message || 'Error al inicializar feeds';
        console.error('Error en refresh:', e);
      } finally {
        this.initialLoading = false;
      }
    },
  },

  async mounted() {
    await this.refresh();

    await this.$nextTick();

    // Agregar listener para resize
    window.addEventListener('resize', this.handleResize);

    // Asegurar que newsMain esté disponible
    setTimeout(() => {
      const newsMain = this.$refs.newsMain;
      if (newsMain) {
        newsMain.addEventListener('scroll', this.handleScroll, { passive: true });
      }
    }, 500);
  },

  beforeUnmount() {
    const newsMain = this.$refs.newsMain;
    if (newsMain) {
      newsMain.removeEventListener('scroll', this.handleScroll);
    }
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
/* Dark Theme */
.news-app {
  min-height: 100vh;
  background: #0e1217;
  color: #e1e7ef;
  display: flex;
  flex-direction: column;
}

/* Main Layout */
.news-layout {
  display: flex;
  flex: 1;
  position: relative;
}

/* News Body */
.news-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 240px;
  height: calc(100vh - 57px);
  max-height: calc(100vh - 57px);
  background: #0e1217;
  transition: margin-left 0.3s ease;
  overflow: hidden;
}

.news-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 80px;
  height: 100%;
  max-height: calc(100vh - 57px);
}

.news-main__container {
  padding: 24px 20px 0;
  display: flex;
  flex-direction: column;
}

.news-content {
  padding-right: 8px;
}

/* Scrollbar */
.news-main::-webkit-scrollbar {
  width: 6px;
}
.news-main::-webkit-scrollbar-track {
  background: #1c2127;
  border-radius: 3px;
}
.news-main::-webkit-scrollbar-thumb {
  background: #2d333b;
  border-radius: 3px;
}
.news-main::-webkit-scrollbar-thumb:hover {
  background: #4a9eff;
}

/* HuggingFace Header */
.huggingface-header {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 32px;
  padding: 24px;
  background: linear-gradient(135deg, rgba(255, 176, 0, 0.1), rgba(229, 154, 0, 0.05));
  border: 1px solid rgba(255, 176, 0, 0.2);
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(255, 176, 0, 0.1);
}

.huggingface-logo {
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.huggingface-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.huggingface-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.huggingface-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.huggingface-title {
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  color: #ffb000;
  line-height: 1.2;
}

.huggingface-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 176, 0, 0.1);
  color: #ffb000;
  text-decoration: none;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;
  border: 1px solid rgba(255, 176, 0, 0.2);
}

.huggingface-link-btn:hover {
  background: rgba(255, 176, 0, 0.2);
  border-color: rgba(255, 176, 0, 0.4);
  transform: scale(1.05);
}

.huggingface-link-btn:active {
  transform: scale(0.95);
}

.huggingface-link-btn svg {
  flex-shrink: 0;
}


.huggingface-description {
  font-size: 15px;
  color: #d1d9e3;
  margin: 0;
  line-height: 1.6;
  font-weight: 400;
}

/* Encabezado de sección (mismo estilo que NewsIA) */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid #2d333b;
}

.section-header__icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-header__icon img {
  filter: brightness(0) invert(1);
  opacity: 0.9;
}

.section-header__main {
  flex: 1;
  min-width: 0;
}

.section-header__top {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #e1e7ef;
  line-height: 1.2;
}

.section-description {
  font-size: 13px;
  color: #7d8694;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.section-count {
  font-size: 13px;
  font-weight: 500;
  color: #7d8694;
  flex-shrink: 0;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  margin-bottom: 20px;
}
.error-icon {
  color: #ef4444;
  flex-shrink: 0;
}
.error-content h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #fca5a5;
}
.error-content p {
  margin: 0;
  font-size: 14px;
  color: #fca5a5;
}

/* Loading Spinner */
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  color: #7d8694;
  font-size: 14px;
  font-weight: 600;
}
.loading-more .loading-spinner {
  border-color: rgba(255, 176, 0, 0.3);
  border-top-color: #ffb000;
}

/* Load More Button */
.load-more-container {
  display: flex;
  justify-content: center;
  padding: 32px 20px;
}
.load-more-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #ffb000, #e59a00);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(255, 176, 0, 0.3);
}
.load-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 176, 0, 0.4);
}
.load-more-btn:active {
  transform: translateY(0);
}
.load-more-btn svg {
  flex-shrink: 0;
  animation: bounce 2s infinite;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}

.end-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  color: #34d399;
  font-size: 14px;
  font-weight: 600;
  background: rgba(52, 211, 153, 0.1);
  border: 1px solid rgba(52, 211, 153, 0.3);
  border-radius: 12px;
  margin-top: 20px;
}

/* Scroll to Top Button */
.scroll-to-top {
  position: fixed;
  bottom: 100px;
  right: 30px;
  width: 56px;
  height: 56px;
  background: white;
  color: black;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(255, 176, 0, 0.5);
  transition: all 0.3s ease;
  z-index: 300;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: scale(0.8);
}

.scroll-to-top--visible {
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
  transform: scale(1) !important;
}

.scroll-to-top--visible:hover {
  transform: scale(1.1) translateY(-3px) !important;
  box-shadow: 0 6px 20px rgba(255, 176, 0, 0.6);
}

.scroll-to-top--visible:active {
  transform: scale(0.95) !important;
}

.scroll-to-top svg {
  width: 24px;
  height: 24px;
  stroke-width: 2.5;
}

/* Mobile Overlay */
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 400;
  backdrop-filter: blur(4px);
}

.sidebar--mobile-visible {
  left: 0 !important;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }

  .news-body {
    margin-left: 0;
  }

  .news-main {
    padding-bottom: 60px;
  }

  .section-header {
    gap: 10px;
    padding-bottom: 12px;
  }

  .section-header__icon {
    width: 20px;
    height: 20px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-description {
    display: none;
  }

  .huggingface-header {
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }

  .huggingface-logo {
    width: 64px;
    height: 64px;
  }

  .huggingface-title-row {
    gap: 12px;
  }

  .huggingface-title {
    font-size: 24px;
  }

  .huggingface-link-btn {
    width: 32px;
    height: 32px;
  }

  .huggingface-link-btn svg {
    width: 16px;
    height: 16px;
  }

  .huggingface-description {
    font-size: 14px;
  }

  .scroll-to-top {
    bottom: 70px;
    right: 16px;
    width: 44px;
    height: 44px;
  }

  .scroll-to-top svg {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .news-main__container {
    padding: 16px 12px 0;
  }

  .huggingface-header {
    padding: 16px;
    gap: 12px;
  }

  .huggingface-logo {
    width: 56px;
    height: 56px;
  }

  .huggingface-title-row {
    gap: 10px;
  }

  .huggingface-title {
    font-size: 20px;
  }

  .huggingface-link-btn {
    width: 30px;
    height: 30px;
  }

  .huggingface-link-btn svg {
    width: 14px;
    height: 14px;
  }


  .huggingface-description {
    font-size: 13px;
  }
}
</style>
